import React, { useState } from 'react';
import axios from 'axios';

const DECISION_TYPES = [
  { value: 'SYSTEM_ALIGNED', label: 'System aligned' },
  { value: 'SYSTEM_OVERRIDE', label: 'System override' },
  { value: 'FORCED', label: 'Forced (no-action override)' },
  { value: 'DEFERRED', label: 'Deferred' },
];



function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Section({ title }) {
  return <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, marginTop: 24, paddingBottom: 6, borderBottom: '1px solid #2d3748' }}>{title}</div>;
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', background: '#0f1520', border: '1px solid #2d3748', borderRadius: 6,
  color: '#f1f5f9', padding: '8px 10px', fontSize: 14, boxSizing: 'border-box',
};

const selectStyle = { ...inputStyle };

export default function ManagePositionModal({ position, onClose, onSaved }) {
  const [action, setAction] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Close leg form state
  const [closeLegId, setCloseLegId] = useState('');
  const [legOutcome, setLegOutcome] = useState('EXPIRED');
  const [buybackPremium, setBuybackPremium] = useState('');

  // Assignment form state
  const [assignShares, setAssignShares] = useState(position.legs ? (position.legs.filter(l => l.outcome === 'OPEN' && l.leg_type === 'SHORT_PUT').reduce((s, l) => s + l.contracts, 0) * 100) : 0);
  const [assignCostBasis, setAssignCostBasis] = useState('');

  // Add leg form state
  const [newLeg, setNewLeg] = useState({
    legType: position.status === 'ASSIGNED' || position.status === 'OPEN_CALL' ? 'SHORT_CALL' : 'SHORT_PUT',
    strike: '',
    expirationDate: '',
    contracts: '1',
    premiumExpected: '',
    premiumActual: '',
    deltaAtOpen: '',
    ivRankAtOpen: '',
    decisionType: 'SYSTEM_ALIGNED',
    overrideReason: '',
  });

  const p = position;
  const openLegs = p.legs ? p.legs.filter(l => l.outcome === 'OPEN') : [];

  // Determine valid actions for current status
  const canCloseLeg = openLegs.length > 0;
  const canAssign = p.status === 'OPEN_PUT' || p.status === 'OPEN_CALL';
  const canAddLeg = ['OPEN_PUT', 'OPEN_CALL', 'ASSIGNED'].includes(p.status);
  const canClosePosition = p.status === 'ASSIGNED' || openLegs.length === 0;

  function setNL(field, value) {
    setNewLeg(f => ({ ...f, [field]: value }));
  }

  async function handleCloseLeg() {
    if (!closeLegId) { setError('Select a leg to close.'); return; }
    if (legOutcome === 'ROLLED' && !buybackPremium) { setError('Enter buyback premium for a roll.'); return; }
    try {
      setSaving(true);
      setError(null);
      const body = { outcome: legOutcome };
      if (legOutcome === 'ROLLED' && buybackPremium) {
        // Deduct buyback cost from position premium
        const deduction = parseFloat(buybackPremium) * 100 * (openLegs.find(l => l.leg_id === closeLegId)?.contracts || 1);
        await axios.patch(`/api/positions/${p.position_id}/add-premium`, { premiumAmount: -deduction });
      }
      await axios.patch(`/api/legs/${closeLegId}/close`, body);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleAssign() {
    if (!assignCostBasis) { setError('Cost basis per share is required.'); return; }
    try {
      setSaving(true);
      setError(null);
      // Close all open put legs as ASSIGNED
      for (const leg of openLegs.filter(l => l.leg_type === 'SHORT_PUT')) {
        await axios.patch(`/api/legs/${leg.leg_id}/close`, { outcome: 'ASSIGNED' });
      }
      await axios.patch(`/api/positions/${p.position_id}/assign`, {
        costBasisPerShare: parseFloat(assignCostBasis),
        sharesHeld: parseInt(assignShares),
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleAddLeg() {
    if (!newLeg.strike || !newLeg.expirationDate || !newLeg.premiumActual) {
      setError('Strike, expiration, and actual premium are required.');
      return;
    }
    const needsReason = newLeg.decisionType === 'SYSTEM_OVERRIDE' || newLeg.decisionType === 'FORCED';
    if (needsReason && !newLeg.overrideReason.trim()) {
      setError('A reason is required for this decision type.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const premiumAmount = parseFloat(newLeg.premiumActual) * 100 * parseInt(newLeg.contracts);
      await axios.post(`/api/positions/${p.position_id}/legs`, {
        legType: newLeg.legType,
        strike: parseFloat(newLeg.strike),
        expirationDate: newLeg.expirationDate,
        contracts: parseInt(newLeg.contracts),
        dteAtOpen: Math.ceil((new Date(newLeg.expirationDate) - new Date()) / (1000 * 60 * 60 * 24)),
        premiumExpected: newLeg.premiumExpected ? parseFloat(newLeg.premiumExpected) : null,
        premiumActual: parseFloat(newLeg.premiumActual),
        deltaAtOpen: newLeg.deltaAtOpen ? parseFloat(newLeg.deltaAtOpen) : null,
        ivRankAtOpen: newLeg.ivRankAtOpen ? parseFloat(newLeg.ivRankAtOpen) : null,
        decisionType: newLeg.decisionType,
        overrideReason: needsReason ? newLeg.overrideReason : null,
      });
      await axios.patch(`/api/positions/${p.position_id}/add-premium`, { premiumAmount });
      // Update status if adding call after assignment
      if (newLeg.legType === 'SHORT_CALL' && p.status === 'ASSIGNED') {
        await axios.patch(`/api/positions/${p.position_id}/status`, { status: 'OPEN_CALL' });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleClosePosition() {
    try {
      setSaving(true);
      setError(null);
      // Close any remaining open legs as EXPIRED
      for (const leg of openLegs) {
        await axios.patch(`/api/legs/${leg.leg_id}/close`, { outcome: 'EXPIRED' });
      }
      await axios.patch(`/api/positions/${p.position_id}/close`, {});
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="form-overlay" onClick={onClose}>
      <div className="form-panel" onClick={e => e.stopPropagation()}>
        <div className="form-header">
          <span className="form-title">{p.ticker} — Manage position</span>
          <button className="form-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 4 }}>
          Status: <span style={{ color: '#f1f5f9' }}>{p.status.replace(/_/g, ' ')}</span>
          {p.shares_held > 0 && <span style={{ marginLeft: 12 }}>{p.shares_held} shares @ ${parseFloat(p.cost_basis_per_share || 0).toFixed(2)} cost basis</span>}
        </div>
        <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
          Premium collected: <span style={{ color: '#4ade80' }}>${parseFloat(p.total_premium_collected || 0).toFixed(2)}</span>
        </div>

        {/* Action selector */}
        {!action && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {canCloseLeg && (
              <button className="btn-submit" style={{ textAlign: 'left', padding: '12px 16px' }} onClick={() => { setCloseLegId(openLegs[0]?.leg_id || ''); setAction('close-leg'); }}>
                Close / roll a leg →
              </button>
            )}
            {canAssign && (
              <button className="btn-submit" style={{ textAlign: 'left', padding: '12px 16px', background: '#92400e' }} onClick={() => setAction('assign')}>
                Log assignment →
              </button>
            )}
            {canAddLeg && (
              <button className="btn-submit" style={{ textAlign: 'left', padding: '12px 16px', background: '#1e3a5f' }} onClick={() => setAction('add-leg')}>
                Add leg (call / roll / new put) →
              </button>
            )}
            {canClosePosition && (
              <button className="btn-cancel" style={{ textAlign: 'left', padding: '12px 16px' }} onClick={() => setAction('close-position')}>
                Close position →
              </button>
            )}
          </div>
        )}

        {/* Close leg */}
        {action === 'close-leg' && (
          <>
            <Section title="Close leg" />
            <Field label="Leg">
              <select style={selectStyle} value={closeLegId} onChange={e => setCloseLegId(e.target.value)}>
                {openLegs.map(l => (
                  <option key={l.leg_id} value={l.leg_id}>
                    {l.leg_type === 'SHORT_PUT' ? 'Short put' : 'Short call'} ${parseFloat(l.strike).toFixed(2)} · {formatDate(l.expiration_date)} · {l.contracts} contract{l.contracts !== 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Outcome">
              <select style={selectStyle} value={legOutcome} onChange={e => setLegOutcome(e.target.value)}>
                <option value="EXPIRED">Expired worthless</option>
                <option value="ROLLED">Rolled (bought back)</option>
                <option value="ASSIGNED">Assigned</option>
              </select>
            </Field>
            {legOutcome === 'ROLLED' && (
              <Field label="Buyback premium per contract (deducted from P&L)">
                <input style={inputStyle} type="number" step="0.01" min="0" placeholder="0.08" value={buybackPremium} onChange={e => setBuybackPremium(e.target.value)} />
              </Field>
            )}
            {error && <div className="form-error">{error}</div>}
            <div className="form-actions">
              <button className="btn-cancel" onClick={() => { setAction(null); setError(null); }}>Back</button>
              <button className="btn-submit" disabled={saving} onClick={handleCloseLeg}>{saving ? 'Saving...' : 'Close leg'}</button>
            </div>
          </>
        )}

        {/* Log assignment */}
        {action === 'assign' && (
          <>
            <Section title="Log assignment" />
            <Field label="Shares assigned">
              <input style={inputStyle} type="number" min="1" value={assignShares} onChange={e => setAssignShares(e.target.value)} />
            </Field>
            <Field label="Cost basis per share (strike − premium per share)">
              <input style={inputStyle} type="number" step="0.01" min="0" placeholder="16.31" value={assignCostBasis} onChange={e => setAssignCostBasis(e.target.value)} />
            </Field>
            {error && <div className="form-error">{error}</div>}
            <div className="form-actions">
              <button className="btn-cancel" onClick={() => { setAction(null); setError(null); }}>Back</button>
              <button className="btn-submit" style={{ background: '#92400e' }} disabled={saving} onClick={handleAssign}>{saving ? 'Saving...' : 'Log assignment'}</button>
            </div>
          </>
        )}

        {/* Add leg */}
        {action === 'add-leg' && (
          <>
            <Section title="Add leg" />
            <Field label="Leg type">
              <select style={selectStyle} value={newLeg.legType} onChange={e => setNL('legType', e.target.value)}>
                <option value="SHORT_PUT">Short put</option>
                <option value="SHORT_CALL">Short call</option>
              </select>
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <Field label="Strike *">
                <input style={inputStyle} type="number" step="0.50" min="0" placeholder="13.00" value={newLeg.strike} onChange={e => setNL('strike', e.target.value)} />
              </Field>
              <Field label="Expiration *">
                <input style={inputStyle} type="date" value={newLeg.expirationDate} onChange={e => setNL('expirationDate', e.target.value)} />
              </Field>
              <Field label="Contracts">
                <input style={inputStyle} type="number" min="1" step="1" value={newLeg.contracts} onChange={e => setNL('contracts', e.target.value)} />
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <Field label="Premium expected">
                <input style={inputStyle} type="number" step="0.01" min="0" placeholder="0.44" value={newLeg.premiumExpected} onChange={e => setNL('premiumExpected', e.target.value)} />
              </Field>
              <Field label="Premium actual *">
                <input style={inputStyle} type="number" step="0.01" min="0" placeholder="0.41" value={newLeg.premiumActual} onChange={e => setNL('premiumActual', e.target.value)} />
              </Field>
              <Field label="Delta at open">
                <input style={inputStyle} type="number" step="0.01" min="0" max="1" placeholder="0.26" value={newLeg.deltaAtOpen} onChange={e => setNL('deltaAtOpen', e.target.value)} />
              </Field>
            </div>
            <Field label="IV rank">
              <input style={inputStyle} type="number" step="1" min="0" max="100" placeholder="58" value={newLeg.ivRankAtOpen} onChange={e => setNL('ivRankAtOpen', e.target.value)} />
            </Field>
            <Field label="Decision type">
              <select style={selectStyle} value={newLeg.decisionType} onChange={e => setNL('decisionType', e.target.value)}>
                {DECISION_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </Field>
            {(newLeg.decisionType === 'SYSTEM_OVERRIDE' || newLeg.decisionType === 'FORCED') && (
              <Field label="Reason *">
                <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} value={newLeg.overrideReason} onChange={e => setNL('overrideReason', e.target.value)} />
              </Field>
            )}
            {error && <div className="form-error">{error}</div>}
            <div className="form-actions">
              <button className="btn-cancel" onClick={() => { setAction(null); setError(null); }}>Back</button>
              <button className="btn-submit" style={{ background: '#1e3a5f' }} disabled={saving} onClick={handleAddLeg}>{saving ? 'Saving...' : 'Add leg'}</button>
            </div>
          </>
        )}

        {/* Close position */}
        {action === 'close-position' && (
          <>
            <Section title="Close position" />
            <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
              This will mark the position as CLOSED.
              {openLegs.length > 0 && ` ${openLegs.length} open leg${openLegs.length !== 1 ? 's' : ''} will be marked EXPIRED.`}
            </p>
            {error && <div className="form-error">{error}</div>}
            <div className="form-actions">
              <button className="btn-cancel" onClick={() => { setAction(null); setError(null); }}>Back</button>
              <button className="btn-submit" style={{ background: '#7f1d1d' }} disabled={saving} onClick={handleClosePosition}>{saving ? 'Closing...' : 'Confirm close'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
